import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { exportToJSON } from '@mobileHelpers'

interface ConfigItem {
  id: string | number
  title: string
  internalId?: string
}

interface UseConfigPageOptions<T extends ConfigItem> {
  items: T[]
  copyItem: (id: string | number, newTitle: string, newInternalId: string) => Promise<T | null>
  deleteItem: (id: string | number) => Promise<void>
  createRoute: string
  editRoute: (id: string | number) => string
  exportFileName: (item: T) => string
  copyDialogTitle: string
}

export const useConfigPage = <T extends ConfigItem>({
  items,
  copyItem,
  deleteItem,
  createRoute,
  editRoute,
  exportFileName,
  copyDialogTitle,
}: UseConfigPageOptions<T>) => {
  const navigate = useNavigate()
  const getDisplayName = (item: ConfigItem): string => item.title

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [itemToCopy, setItemToCopy] = useState<{
    id: string | number
    title: string
    internalId?: string
  } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ id: string | number; name: string } | null>(
    null
  )

  const handleCreate = useCallback(() => {
    navigate(createRoute)
  }, [navigate, createRoute])

  const handleEdit = useCallback(
    (id: string | number) => {
      navigate(editRoute(id))
    },
    [navigate, editRoute]
  )

  const handleCopyClick = useCallback(
    (id: string | number) => {
      const item = items.find(i => String(i.id) === String(id))
      if (item) {
        setItemToCopy({ id: item.id, title: getDisplayName(item), internalId: item.internalId })
        setCopyDialogOpen(true)
      }
    },
    [items]
  )

  const handleCopy = useCallback(
    async (newTitle: string, newInternalId: string) => {
      if (itemToCopy) {
        const newItem = await copyItem(itemToCopy.id, newTitle, newInternalId)
        if (newItem) {
          setCopyDialogOpen(false)
          setItemToCopy(null)
          navigate(editRoute(newItem.id))
        }
      }
    },
    [itemToCopy, copyItem, navigate, editRoute]
  )

  const handleCopyDialogClose = useCallback(() => {
    setCopyDialogOpen(false)
    setItemToCopy(null)
  }, [])

  const handleDeleteClick = useCallback(
    (id: string | number) => {
      const item = items.find(i => String(i.id) === String(id))
      if (item) {
        setItemToDelete({ id: item.id, name: getDisplayName(item) })
        setDeleteDialogOpen(true)
      }
    },
    [items]
  )

  const handleDelete = useCallback(async () => {
    if (itemToDelete) {
      await deleteItem(itemToDelete.id)
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }, [itemToDelete, deleteItem])

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpen(false)
    setItemToDelete(null)
  }, [])

  const handleExport = useCallback(
    (id: string | number) => {
      const item = items.find(i => String(i.id) === String(id))
      if (item) {
        exportToJSON(item, exportFileName(item))
      }
    },
    [items, exportFileName]
  )

  return {
    // State
    copyDialogOpen,
    itemToCopy,
    deleteDialogOpen,
    itemToDelete,
    // Handlers
    handleCreate,
    handleEdit,
    handleCopyClick,
    handleCopy,
    handleCopyDialogClose,
    handleDeleteClick,
    handleDelete,
    handleDeleteDialogClose,
    handleExport,
    // Dialog props
    copyDialogProps: {
      open: copyDialogOpen,
      title: copyDialogTitle,
      initialName: itemToCopy?.title || '',
      existingNames: items.map(i => getDisplayName(i)),
      initialInternalId: itemToCopy?.internalId,
      existingInternalIds: items.map(i => i.internalId).filter(Boolean) as string[],
      onClose: handleCopyDialogClose,
      onConfirm: handleCopy,
    },
    deleteDialogProps: {
      open: deleteDialogOpen,
      itemToDelete,
      onClose: handleDeleteDialogClose,
      onConfirm: handleDelete,
    },
  }
}
